import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CONTENT_TYPE } from '../constants/docs';

import { useDocsPageContext } from '../hooks/useDocsPageContext';

import ErrorAlert from '../design-system/ErrorAlert/ErrorAlert';
import MoveToPanel from '../pages/DocsPage/MoveToPanel/MoveToPanel';
import SendToOrgModal from '../pages/DocsPage/SendToOrgModal/SendToOrgModal';
import RenameItemModal from '../pages/DocsPage/RenameItemModal/RenameItemModal';
import ArchiveItemModal from '../pages/DocsPage/ArchiveItemModal/ArchiveItemModal';
import SuccessSentToAlert from '../pages/DocsPage/SuccessSentToAlert/SuccessSentToAlert';
import SuccessArchiveAlert from '../pages/DocsPage/SuccessArchiveAlert/SuccessArchiveAlert';

const actionModalEmptyState = {
    action: null,
    contentType: null,
    data: null,
};
const successArchiveAlertEmptyState = {
    archivedItems: null, // [] of archived items
    parentFolderLocation: null,
};

// should be wrapped with DocsPageProvider
export function withFoldersAndDocsActions(WrappedComponent) {
    return function WithFoldersAndDocsActions(props) {
        const { folderId } = useParams();
        const navigate = useNavigate();

        const [actionModal, setActionModal] = useState(actionModalEmptyState);
        const [errorAlert, setErrorAlert] = useState(null);

        const [successArchiveAlert, setSuccessArchiveAlert] = useState(
            successArchiveAlertEmptyState
        );
        const [successSentToAlert, setSuccessSentToAlert] = useState(null);

        const {
            requestKeysToMutate,
            currentRequestKeyToMutate,
            location,
            setLocation,
            setSelectedItems,
        } = useDocsPageContext();

        const navigateToRoot = () => {
            navigate('/docs');
            setLocation([]);
        };

        const navigateToPrevIfCurrentFolderRemoved = ({ removedItemId, contentType }) => {
            if (contentType === CONTENT_TYPE.folder) {
                const isCurrentFolderRemoved =
                    removedItemId === folderId || location?.some(({ id }) => id === removedItemId);

                if (isCurrentFolderRemoved) {
                    const index = location.findIndex(({ id }) => id === removedItemId);

                    const updatedLocation = index >= 0 ? location.slice(0, index) : [];
                    const id = updatedLocation[updatedLocation.length - 1]?.id;

                    if (id) {
                        navigate(`/docs/folder/${id}`);
                        setLocation(updatedLocation);
                    } else {
                        navigateToRoot();
                    }
                }
            }
        };

        const removeItemFromSelectedList = ({ removedItemId }) => {
            setSelectedItems((prevSelectedItems) => {
                const isRemovedItemSelected = !!prevSelectedItems[removedItemId];
                if (!isRemovedItemSelected) return prevSelectedItems;

                const newSelectedItems = { ...prevSelectedItems };
                delete newSelectedItems[removedItemId];
                return newSelectedItems;
            });
        };

        const onArchiveSuccess = ({ id: removedItemId, contentType }) => {
            navigateToPrevIfCurrentFolderRemoved({ removedItemId, contentType });

            removeItemFromSelectedList({ removedItemId });
        };

        const onSendSuccess = ({ id, contentType }) => {
            navigateToPrevIfCurrentFolderRemoved({ removedItemId: id, contentType });

            removeItemFromSelectedList({ removedItemId: id });
        };

        const onRenameSuccess = ({ id, updatedLabel }) => {
            // rename item if it is selected
            setSelectedItems((prevSelectedItems) => {
                const isCurrentItemSelected = !!prevSelectedItems[id];

                if (!isCurrentItemSelected) return prevSelectedItems;

                return {
                    ...prevSelectedItems,
                    [id]: { ...prevSelectedItems[id], label: updatedLabel },
                };
            });

            const isCurrentItemInLocation = !!location?.find((folder) => folder.id === id);
            if (isCurrentItemInLocation) {
                setLocation((currentLocation) =>
                    currentLocation.map((folder) =>
                        folder.id === id
                            ? {
                                  ...folder,
                                  label: updatedLabel,
                              }
                            : folder
                    )
                );
            }
        };

        return (
            <>
                <WrappedComponent
                    {...props}
                    setActionModal={setActionModal}
                    setSuccessSentToAlert={setSuccessSentToAlert}
                    setSuccessArchiveAlert={setSuccessArchiveAlert}
                />

                {actionModal.action === 'move' && (
                    <MoveToPanel
                        itemsToMove={[actionModal.data]}
                        currentRequestKeyToMutate={currentRequestKeyToMutate}
                        requestKeysToMutate={requestKeysToMutate}
                        viewType="modal"
                        movedItemsParentLocation={actionModal.data?.parentLocation}
                        setSelectedItems={setSelectedItems}
                        onClose={() => setActionModal(actionModalEmptyState)}
                    />
                )}

                {actionModal.action === 'send' && (
                    <SendToOrgModal
                        contentType={actionModal.contentType}
                        data={actionModal.data}
                        requestKeysToMutate={requestKeysToMutate}
                        setSuccessAlert={setSuccessSentToAlert}
                        onSendSuccess={onSendSuccess}
                        onClose={() => setActionModal(actionModalEmptyState)}
                    />
                )}

                {actionModal.action === 'rename' && (
                    <RenameItemModal
                        contentType={actionModal.contentType}
                        data={actionModal.data}
                        requestKeysToMutate={requestKeysToMutate}
                        onRenameSuccess={onRenameSuccess}
                        onClose={() => setActionModal(actionModalEmptyState)}
                    />
                )}

                {actionModal.action === 'archive' && (
                    <ArchiveItemModal
                        contentType={actionModal.contentType}
                        data={actionModal.data}
                        requestKeysToMutate={requestKeysToMutate}
                        setSuccessArchiveAlert={setSuccessArchiveAlert}
                        onArchiveSuccess={onArchiveSuccess}
                        onClose={() => setActionModal(actionModalEmptyState)}
                    />
                )}

                {successArchiveAlert.archivedItems && (
                    <SuccessArchiveAlert
                        archivedItems={successArchiveAlert.archivedItems}
                        parentFolderLocation={successArchiveAlert.parentFolderLocation}
                        requestKeysToMutate={requestKeysToMutate}
                        currentRequestKeyToMutate={currentRequestKeyToMutate}
                        setErrorAlert={setErrorAlert}
                        handleClose={() => setSuccessArchiveAlert(successArchiveAlertEmptyState)}
                    />
                )}

                {successSentToAlert && (
                    <SuccessSentToAlert
                        data={successSentToAlert.data}
                        onClose={() => setSuccessSentToAlert(null)}
                    />
                )}

                <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
            </>
        );
    };
}
